import React from 'react';
import { DataTable } from '../../components/ui/DataTable';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { StatusPill } from '../../components/ui/StatusPill';
import { getFleetManagers } from '../../services/admin';

const PAGE_SIZE = 50;

type FleetManagerRecord = {
  id: string;
  initials: string;
  name: string;
  username: string;
  email: string;
  phone: string;
  requestedRole: string;
  role: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  fleet: string;
  drivers: number;
  vehicles: number;
  status: string;
  joined: string;
};

export function FleetManagers({ searchQuery = '' }: { searchQuery?: string }) {
  const [managers, setManagers] = React.useState<FleetManagerRecord[]>([]);
  const [selectedManager, setSelectedManager] = React.useState<FleetManagerRecord | null>(null);
  const [status, setStatus] = React.useState('');
  const [search, setSearch] = React.useState(searchQuery.trim());
  const [offset, setOffset] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchQuery.trim());
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  React.useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError('');

    getFleetManagers({ limit: PAGE_SIZE, offset, search, status })
      .then((response) => {
        if (!isActive) return;
        const records = normalizeFleetManagers(response);
        setManagers(records);
        setTotal(readNumber(response, ['total', 'count']) ?? records.length);
      })
      .catch((requestError) => {
        if (!isActive) return;
        setManagers([]);
        setError(requestError instanceof Error ? requestError.message : 'Unable to load fleet managers');
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => { isActive = false; };
  }, [offset, search, status]);

  const pageStart = total ? offset + 1 : 0;
  const pageEnd = Math.min(offset + managers.length, total);

  return (
    <div className="passengers-page">
      <SectionHeader
        actions={(
          <div className="passenger-filters">
          <label>
            <span className="sr-only">Filter fleet managers by status</span>
            <select value={status} onChange={(event) => { setStatus(event.target.value); setOffset(0); }}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
            <StatusPill tone="info">{managers.length} shown</StatusPill>
          </div>
        )}
        className="section-head passengers-head"
        subtitle="View fleet manager accounts and their assigned fleet resources."
        title="Fleet managers"
      />

      {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}

      <DataTable className="passengers-table">
          <thead>
            <tr>
              <th>Fleet manager</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Verification</th>
              <th>Status</th>
              <th>Joined</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="kyc-table-state" colSpan={8}>Loading fleet managers...</td></tr>
            ) : managers.length ? managers.map((manager) => (
              <tr key={manager.id}>
                <td data-label="Fleet manager">
                  <div className="row-cell">
                    <span className="row-avatar">{manager.initials}</span>
                    <span className="row-info"><strong>{manager.name}</strong><span>{manager.email}</span></span>
                  </div>
                </td>
                <td data-label="Phone">{manager.phone}</td>
                <td data-label="Role"><StatusPill tone="info">{formatRole(manager.requestedRole)}</StatusPill></td>
                <td data-label="Verification">
                  <div className="passenger-verification">
                    <StatusPill tone={manager.emailVerified ? 'success' : 'muted'}>email</StatusPill>
                    <StatusPill tone={manager.phoneVerified ? 'success' : 'muted'}>phone</StatusPill>
                  </div>
                </td>
                <td data-label="Status"><StatusPill tone={manager.status === 'active' ? 'success' : 'danger'}>{manager.status}</StatusPill></td>
                <td data-label="Joined">{manager.joined}</td>
                <td>
                  <button aria-label={`View ${manager.name}`} className="btn btn-sm btn-outline" type="button" onClick={() => setSelectedManager(manager)}>View</button>
                </td>
              </tr>
            )) : (
              <tr><td className="kyc-table-state" colSpan={8}>{search ? `No fleet managers match "${search}".` : 'No fleet managers found.'}</td></tr>
            )}
          </tbody>
      </DataTable>

      <div className="passengers-pagination">
        <span>Showing {pageStart}-{pageEnd} of {total.toLocaleString()}</span>
        <div>
          <button className="btn" disabled={offset === 0 || isLoading} type="button" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
          <button className="btn" disabled={offset + PAGE_SIZE >= total || isLoading} type="button" onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
        </div>
      </div>

      {selectedManager ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setSelectedManager(null)}>
          <section aria-labelledby="fleet-manager-details-title" aria-modal="true" className="signup-dialog passenger-details-dialog" role="dialog" onMouseDown={(event) => event.stopPropagation()}>
            <div className="signup-dialog-head">
              <div><span className="row-avatar">{selectedManager.initials}</span><div><h2 id="fleet-manager-details-title">{selectedManager.name}</h2><StatusPill tone={selectedManager.status === 'active' ? 'success' : 'danger'}>{selectedManager.status}</StatusPill></div></div>
              <button aria-label="Close fleet manager details" className="btn-icon borderless" type="button" onClick={() => setSelectedManager(null)}>&times;</button>
            </div>
            <dl className="kyc-details">
              <Detail label="Username" value={selectedManager.username} />
              <Detail label="Email" value={selectedManager.email} />
              <Detail label="Phone" value={selectedManager.phone} />
              <Detail label="Requested role" value={formatRole(selectedManager.requestedRole)} />
              <Detail label="Current role" value={formatRole(selectedManager.role)} />
              <Detail label="Email verified" value={selectedManager.emailVerified ? 'Yes' : 'No'} />
              <Detail label="Phone verified" value={selectedManager.phoneVerified ? 'Yes' : 'No'} />
              <Detail label="Assigned fleet" value={selectedManager.fleet} />
              <Detail label="Drivers" value={selectedManager.drivers.toLocaleString()} />
              <Detail label="Vehicles" value={selectedManager.vehicles.toLocaleString()} />
              <Detail label="Joined" value={selectedManager.joined} />
              <Detail label="User ID" value={selectedManager.id} />
            </dl>
            <div className="kyc-dialog-actions"><button className="btn" type="button" onClick={() => setSelectedManager(null)}>Close</button></div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

export function normalizeFleetManagers(response: Record<string, unknown>) {
  const source: Record<string, unknown>[] = readArray(response, ['fleet_managers', 'fleetManagers', 'managers', 'users', 'items', 'results']);
  return source.map((item: Record<string, unknown>, index: number): FleetManagerRecord => {
    const id = readString(item, ['user_id', 'id', '_id']) ?? `fleet-manager-${index}`;
    const name = readString(item, ['full_name', 'name', 'username', 'email']) ?? 'Fleet manager';
    const isActive = readBoolean(item, ['is_active']);
    const status = isActive === false ? 'inactive' : readString(item, ['status'])?.toLowerCase() ?? 'active';
    const joined = readString(item, ['created_at', 'joined_at']);
    return {
      id,
      initials: getInitials(name),
      name,
      username: readString(item, ['username']) ?? 'Not provided',
      email: readString(item, ['email']) ?? 'Not provided',
      phone: readString(item, ['phone_number', 'phone']) ?? 'Not provided',
      requestedRole: readString(item, ['requested_role']) ?? 'fleet_manager',
      role: readString(item, ['role']) ?? 'Not provided',
      emailVerified: readBoolean(item, ['is_email_verified', 'email_verified']) ?? false,
      phoneVerified: readBoolean(item, ['is_phone_verified', 'phone_verified']) ?? false,
      fleet: readString(item, ['fleet_name', 'fleet', 'company_name']) ?? 'Not assigned',
      drivers: readNumber(item, ['drivers_total', 'driver_count', 'total_drivers']) ?? 0,
      vehicles: readNumber(item, ['vehicles_total', 'vehicle_count', 'total_vehicles']) ?? 0,
      status,
      joined: formatTime(joined),
    };
  });
}

function Detail({ label, value }: { label: string; value: string }) {
  return <div><dt>{label}</dt><dd>{value}</dd></div>;
}

function readArray(source: Record<string, unknown>, keys: string[]): Record<string, unknown>[] {
  for (const key of keys) {
    if (Array.isArray(source[key])) return source[key] as Record<string, unknown>[];
  }
  const data = source.data;
  return data && typeof data === 'object' ? readArray(data as Record<string, unknown>, keys) : [];
}

function formatRole(value: string) {
  return value.replace(/_/g, ' ');
}

function readString(source: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
}

function readNumber(source: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
}

function readBoolean(source: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    if (typeof source[key] === 'boolean') return source[key] as boolean;
  }
  return undefined;
}

function getInitials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'FM';
}

function formatTime(value?: string) {
  if (!value) return 'Not provided';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
